import React, { useState } from "react";
import { Link } from "react-router";
import LanguageSelector from "../../../../shared/ui/header/LanguageSelector";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Quote, Sparkles } from "lucide-react";

// Therapist (default) — blue/purple/pink
const DEFAULT_SCHEME = {
  bgGradient: "from-zen-teal/20 via-zen-blue/20 to-zen-indigo/20",
  blob1: "from-zen-purple/40 to-zen-pink/40",
  blob2: "from-zen-teal/40 to-zen-blue/40",
  blob3Color: "#6A4FD8",
  divider: "from-zen-teal to-zen-blue",
  sparkle: "text-zen-purple",
  titleGradient: "from-zen-blue via-zen-indigo to-zen-pink",
  quoteIcon: "text-zen-purple/30",
} as const;

// Patient portal — same palette, pink/teal lead instead of blue/purple
const PORTAL_SCHEME = {
  bgGradient: "from-zen-pink/20 via-zen-teal/20 to-zen-blue/20",
  blob1: "from-zen-teal/40 to-zen-blue/40",
  blob2: "from-zen-pink/40 to-zen-purple/40",
  blob3Color: "#35C6C8",
  divider: "from-zen-pink to-zen-teal",
  sparkle: "text-zen-teal",
  titleGradient: "from-zen-pink via-zen-purple to-zen-teal",
  quoteIcon: "text-zen-teal/30",
} as const;

export default function AuthLayout({
  children,
  hideLogo = false,
  hideLanguageSelector = false,
  hideVisualPanel = false,
  wrapperClassName = "max-w-md",
  panelTagline,
  panelTitle,
  colorScheme = "default",
}: {
  children: React.ReactNode;
  hideLogo?: boolean;
  hideLanguageSelector?: boolean;
  hideVisualPanel?: boolean;
  wrapperClassName?: string;
  panelTagline?: string;
  panelTitle?: string;
  colorScheme?: "default" | "portal";
}) {
  const { t } = useTranslation();
  const colors = colorScheme === "portal" ? PORTAL_SCHEME : DEFAULT_SCHEME;

  const [randomQuote] = useState(() => {
    const quotes = t("home.quotes", { returnObjects: true }) as string[];
    if (!Array.isArray(quotes) || quotes.length === 0) return "";
    return quotes[Math.floor(Math.random() * quotes.length)];
  });

  return (
    <div className="min-h-screen w-full flex bg-white dark:bg-gray-950">
      {/* Visual Left Panel (Desktop only) */}
      {!hideVisualPanel && (
        <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden flex-col justify-center px-16 xl:px-24 bg-zen-bg dark:bg-gray-950">
          {/* Luminous Background Layer */}
          <div className="absolute inset-0 z-0">
            <div className={`absolute inset-0 bg-gradient-to-br ${colors.bgGradient} opacity-50`} />

            {/* Animated decorative blobs */}
            <motion.div
              animate={{
                scale: [1, 1.2, 1],
                rotate: [0, 90, 0],
                x: [0, 50, 0],
                y: [0, -50, 0],
              }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              className={`absolute -top-24 -right-24 w-[500px] h-[500px] bg-gradient-to-br ${colors.blob1} rounded-full blur-[100px] opacity-40`}
            />
            <motion.div
              animate={{
                scale: [1.2, 1, 1.2],
                rotate: [0, -90, 0],
                x: [0, -40, 0],
                y: [0, 40, 0],
              }}
              transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
              className={`absolute -bottom-32 -left-32 w-[600px] h-[600px] bg-gradient-to-tr ${colors.blob2} rounded-full blur-[120px] opacity-40`}
            />
            <div
              className="absolute top-1/2 left-1/4 w-[400px] h-[400px] rounded-full blur-[100px] opacity-30"
              style={{ backgroundColor: colors.blob3Color + "33" }}
            />
          </div>

          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="relative z-10"
          >
            {!hideLogo && (
              <div className="mb-12">
                <Link to="/">
                  <img
                    src="/images/logo/logo.png"
                    alt="Alchely Logo"
                    className="h-12 w-auto brightness-110 dark:brightness-0 dark:invert"
                  />
                </Link>
              </div>
            )}

            <div className="max-w-lg">
              <div className="flex items-center gap-2 mb-6">
                <div className={`h-[1.5px] w-12 bg-gradient-to-r ${colors.divider}`} />
                <Sparkles className={`w-5 h-5 ${colors.sparkle}`} />
                <span className="text-gray-600 dark:text-white/60 text-xs font-bold uppercase tracking-[0.2em] leading-none">
                  {panelTagline ?? t("auth.panelTagline")}
                </span>
              </div>

              <h2 className="text-4xl xl:text-5xl font-bold text-content-primary mb-8 tracking-tight leading-[1.1]">
                <span className={`bg-gradient-to-br ${colors.titleGradient} bg-clip-text text-transparent`}>
                  {panelTitle ?? t("auth.panelTitle")}
                </span>
              </h2>

              <div className="space-y-6">
                <div className="p-8 rounded-2xl bg-white/40 dark:bg-white/5 backdrop-blur-3xl border border-white/60 dark:border-white/10 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.08)] group transition-all duration-500 hover:shadow-2xl hover:bg-white/50">
                  <Quote className={`w-8 h-8 ${colors.quoteIcon} mb-6 group-hover:scale-110 transition-transform`} />
                  <p className="text-xl xl:text-2xl text-content-primary font-medium italic leading-relaxed">
                    {randomQuote}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Branding Footer */}
          <div className="absolute bottom-12 left-16 xl:left-24 z-10">
            <p className="text-content-tertiary dark:text-white/40 text-sm font-medium">
              {t("auth.copyright", { year: new Date().getFullYear() })}
            </p>
          </div>
        </div>
      )}

      {/* Form Area panel */}
      <div className="flex-1 flex flex-col relative bg-gray-50/50 dark:bg-transparent">
        {/* Mobile Header / Language Selector */}
        <div className="flex items-center justify-between p-6 lg:px-12">
          {!hideLogo && (
            <div className={hideVisualPanel ? "" : "lg:hidden"}>
              <Link to="/" className="group">
                <img
                  src="/images/logo/logo.png"
                  alt="Logo"
                  className="h-8 w-auto dark:brightness-0 dark:invert group-hover:scale-105 transition-transform"
                />
              </Link>
            </div>
          )}
          <div className="ml-auto">
            {!hideLanguageSelector && <LanguageSelector />}
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className={`w-full ${wrapperClassName}`}
          >
            {children}
          </motion.div>
        </div>

        {/* Mobile Footer */}
        <div className="p-6 lg:hidden text-center">
          <p className="text-content-tertiary text-xs font-medium">
            © {new Date().getFullYear()} Alchely.
          </p>
        </div>
      </div>
    </div>
  );
}
